import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { BarChart3, ExternalLink, ArrowRight, ChevronDown, X as XIcon } from 'lucide-react';
import { toast } from 'react-toastify';
import { useTheme } from '../../context/ThemeContext';
import { Card, CardHeader } from '../Card';
import { ModalOverlay, ModalContent, ModalHeader, ModalFooter } from '../ModalStyles';
import { StandaloneVizFrame, openVizInNewTab } from './StandaloneVizFrame';
import { VizShareLinks } from './VizShareLinks';
import standaloneVizService, { vizType } from '../../api/services/standaloneVizService';

/**
 * Submission-detail gadget for the standalone (Preact + htm) viz track. Cards and full visualizations are
 * INDEPENDENT here:
 *  - Every compact CARD an admin flagged "show in submissions" (and that has a design) renders LIVE INLINE,
 *    small and auto-height, for THIS submission — as many as are flagged.
 *  - Whenever the form has any full VISUALIZATION with a design, a "Visualization" launcher is offered
 *    (independent of cards). One viz opens directly; several show a picker. The chosen viz opens FIRST in a
 *    modal; the modal carries an "Open in new tab" affordance (the self-contained standalone doc).
 *
 * Degrades quietly: nothing to show → a compact "create one" prompt; the standalone-viz feature unavailable
 * on the API → renders nothing.
 *
 * @param {{ formSchemaId: number, submissionId: number }} props
 */
export function StandaloneVizSubmissionCard({ formSchemaId, submissionId }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [unavailable, setUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [vizList, setVizList] = useState([]);

  const [menuOpen, setMenuOpen] = useState(false);
  const [modalViz, setModalViz] = useState(null); // the full viz currently open in the modal (null = closed)
  const [opening, setOpening] = useState(false);
  const launcherRef = useRef(null);

  const load = useCallback(async () => {
    if (!formSchemaId) return;
    setLoading(true);
    try {
      const list = await standaloneVizService.listVisualizations(formSchemaId);
      setVizList(Array.isArray(list) ? list : []);
      setUnavailable(false);
    } catch {
      // Feature may not be deployed on older API builds — hide quietly.
      setUnavailable(true);
      setVizList([]);
    } finally {
      setLoading(false);
    }
  }, [formSchemaId]);

  useEffect(() => { load(); }, [load]);

  // Close the picker popover on an outside click.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocClick = (e) => {
      if (launcherRef.current && !launcherRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  // Compact cards to render inline: every card flagged show-in-submission that actually has a design.
  // The list already arrives default-first then most-recently-updated.
  const cards = vizList.filter(
    (v) => vizType(v) === 'card' && v.hasDesign && v.showInSubmission !== false
  );

  // Full visualizations the "Visualization" launcher can open (default first).
  const fullVizzes = vizList.filter((v) => vizType(v) === 'visualization' && v.hasDesign);

  const hasAnything = cards.length > 0 || fullVizzes.length > 0;
  const dashboardPath = `/forms/${formSchemaId}/visualizations`;

  // Share API for the in-modal link control — public, revocable links for THIS submission (the old standalone
  // share card now lives here, in the modal, instead of on the submission page).
  // NOTE: declared BEFORE any early return so the hook order is stable across renders.
  const shareApi = useMemo(() => {
    if (!modalViz) return null;
    return {
      list: () => standaloneVizService.listShares(submissionId),
      create: (days) => standaloneVizService.createShare(submissionId, days),
      revoke: (id) => standaloneVizService.revokeShare(id),
    };
  }, [submissionId, modalViz]);

  // Hide entirely on unsupported API builds rather than show a broken card.
  if (unavailable && !loading) return null;

  const openModalFor = (viz) => {
    setMenuOpen(false);
    setModalViz(viz);
  };

  const onLaunchClick = () => {
    if (fullVizzes.length === 1) openModalFor(fullVizzes[0]);
    else setMenuOpen((o) => !o);
  };

  const handleOpenNewTab = async () => {
    if (!modalViz || opening) return;
    setOpening(true);
    try {
      await openVizInNewTab(submissionId, modalViz.id);
    } catch {
      toast.error('Could not open the visualization.');
    } finally {
      setOpening(false);
    }
  };

  return (
    <>
      <Card theme={theme}>
        <CardHeader theme={theme}>
          <Title theme={theme}>
            <BarChart3 size={16} />
            Visualizations
          </Title>

          {fullVizzes.length > 0 && (
            <Launcher ref={launcherRef}>
              <LaunchBtn theme={theme} type="button" onClick={onLaunchClick}>
                <BarChart3 size={14} />
                Visualization
                {fullVizzes.length > 1 && <ChevronDown size={14} />}
              </LaunchBtn>
              {menuOpen && fullVizzes.length > 1 && (
                <Menu theme={theme} role="menu">
                  {fullVizzes.map((v) => (
                    <MenuItem
                      key={v.id}
                      theme={theme}
                      type="button"
                      role="menuitem"
                      onClick={() => openModalFor(v)}
                    >
                      {v.name}
                      {v.isDefault ? <DefaultTag theme={theme}>Default</DefaultTag> : null}
                    </MenuItem>
                  ))}
                </Menu>
              )}
            </Launcher>
          )}
        </CardHeader>

        {loading ? (
          <Muted theme={theme}>Loading…</Muted>
        ) : hasAnything ? (
          <>
            {cards.length > 0 ? (
              <CardList theme={theme}>
                {cards.map((c) => (
                  <CardItem key={c.id}>
                    <CardItemName theme={theme}>{c.name}</CardItemName>
                    <CardFrameWrap theme={theme}>
                      <StandaloneVizFrame
                        submissionId={submissionId}
                        vizId={c.id}
                        minHeight={120}
                        maxHeight={520}
                        title={c.name}
                      />
                    </CardFrameWrap>
                  </CardItem>
                ))}
              </CardList>
            ) : (
              <Hint theme={theme}>
                No compact cards are shown here. Use <strong>Visualization</strong> above to open the full report.
              </Hint>
            )}

            <Footer theme={theme}>
              <LinkBtn theme={theme} type="button" onClick={() => navigate(dashboardPath)}>
                Manage <ArrowRight size={14} />
              </LinkBtn>
            </Footer>
          </>
        ) : (
          <>
            <Hint theme={theme}>
              No visualizations yet — create a compact card or a full visualization to summarize this submission.
            </Hint>
            <LinkBtn theme={theme} type="button" onClick={() => navigate(`${dashboardPath}#cards`)}>
              Create one <ArrowRight size={14} />
            </LinkBtn>
          </>
        )}
      </Card>

      {modalViz && (
        <ModalOverlay theme={theme} $isOpen onClick={() => setModalViz(null)}>
          <VizModalContent theme={theme} onClick={(e) => e.stopPropagation()}>
            <VizModalHeader theme={theme}>
              <Title theme={theme}>
                <BarChart3 size={16} />
                {modalViz.name}
              </Title>
              <CloseBtn theme={theme} type="button" onClick={() => setModalViz(null)} aria-label="Close">
                <XIcon size={18} />
              </CloseBtn>
            </VizModalHeader>

            <VizModalBody theme={theme}>
              <StandaloneVizFrame
                submissionId={submissionId}
                vizId={modalViz.id}
                fitContent
                minHeight={400}
                title={modalViz.name}
              />
            </VizModalBody>

            <VizModalFooter theme={theme}>
              {shareApi && <VizShareLinks api={shareApi} />}
              <OpenBtn theme={theme} type="button" onClick={handleOpenNewTab} disabled={opening}>
                <ExternalLink size={14} /> {opening ? 'Opening…' : 'Open in new tab'}
              </OpenBtn>
            </VizModalFooter>
          </VizModalContent>
        </ModalOverlay>
      )}
    </>
  );
}

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.xs};
  font-weight: 600;
  font-size: 15px;
  color: ${p => p.theme.colors.text};
  min-width: 0;
  span, & { overflow: hidden; text-overflow: ellipsis; }
`;

const Hint = styled.p`
  margin: 0 0 ${p => p.theme.spacing.md};
  font-size: 13px;
  line-height: 1.5;
  color: ${p => p.theme.colors.muted};
`;

const Muted = styled.div`
  font-size: 13px;
  color: ${p => p.theme.colors.muted};
  padding: ${p => p.theme.spacing.sm} 0;
`;

// Stacks each shown card as a contained unit (name + inline frame).
const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing.md};
  margin-top: ${p => p.theme.spacing.sm};
`;

const CardItem = styled.div`
  min-width: 0;
`;

const CardItemName = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${p => p.theme.colors.muted};
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Frames the inline card so it reads as a contained unit at sidebar width (~302px inner). The frame's own
// width is 100% of this wrapper; full mode auto-heights between minHeight/maxHeight.
const CardFrameWrap = styled.div`
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  overflow: hidden;
  background: #fff;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${p => p.theme.spacing.md};
  flex-wrap: wrap;
  margin-top: ${p => p.theme.spacing.md};
`;

const LinkBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.theme.colors.primary};
  cursor: pointer;
  &:hover { color: ${p => p.theme.colors.tint}; }
  &:disabled { opacity: 0.6; cursor: default; }
`;

// ── Visualization launcher (header) + picker popover ───────────────────────────
const Launcher = styled.div`
  position: relative;
`;

const LaunchBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 10px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.sm};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.primary};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover { border-color: ${p => p.theme.colors.primary}; }
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 50;
  min-width: 200px;
  max-width: 280px;
  background: ${p => p.theme.colors.background};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  box-shadow: ${p => p.theme.shadows.lg};
  padding: 4px;
  display: flex;
  flex-direction: column;
`;

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${p => p.theme.spacing.sm};
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  border: none;
  background: none;
  border-radius: ${p => p.theme.borderRadius.sm};
  font-size: 13px;
  color: ${p => p.theme.colors.text};
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  &:hover { background: ${p => p.theme.colors.backgroundSecondary}; }
`;

const DefaultTag = styled.span`
  flex: 0 0 auto;
  font-size: 10px;
  font-weight: 700;
  color: ${p => p.theme.colors.tint};
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

// ── Modal: full visualization, scrollable, with an open-in-new-tab footer ──────
const VizModalContent = styled(ModalContent)`
  max-width: 1100px;
  width: 95vw;
  height: 85vh;
  max-height: 85vh;
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const VizModalHeader = styled(ModalHeader)`
  padding: ${p => p.theme.spacing.lg} ${p => p.theme.spacing.lg} 0;
  margin-bottom: ${p => p.theme.spacing.md};
`;

const VizModalBody = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 0 ${p => p.theme.spacing.lg};
`;

const VizModalFooter = styled(ModalFooter)`
  padding: 0 ${p => p.theme.spacing.lg} ${p => p.theme.spacing.lg};
  margin-top: ${p => p.theme.spacing.md};
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${p => p.theme.colors.muted};
  padding: 4px;
  display: inline-flex;
  align-items: center;
  &:hover { color: ${p => p.theme.colors.text}; }
`;

const OpenBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid ${p => p.theme.colors.primary};
  border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.theme.colors.primary};
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;
  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.6; cursor: default; }
`;

export default StandaloneVizSubmissionCard;
